import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
/*
 * The schema AND its type, from the contract the route validates with — so this screen cannot
 * drift from the endpoint, in either direction. It was a type-only import for a few hours
 * because the web build could not see a runtime export newly added to the contracts barrel;
 * that was a packaging fault, not a reason to restate the rule here, and it is fixed.
 */
import { zRecordTerm, type RecordTerm } from '@miraki/contracts';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Money, Note, PageHeader, Panel, Spinner, Table, type Tone } from '../ui';
import { invalidateAll } from '../api/write-keys';

/**
 * A-41 Subscriptions — SB-01…SB-06. Admin's view of who has paid this year's fee, who is due,
 * and who has lapsed, on a portal whose capabilities include subscriptions. On any other portal
 * the API answers 404 and the page row is never granted, so this screen never renders there.
 *
 * STATUS IS NOT EDITED HERE, because it is not stored (subscription/subscription-status.ts).
 * Admin records the facts — a term, its fee paid, a cancellation — and the status follows from
 * them and from today's date in the portal's zone. There is no "set status" control to drift
 * out of step with the dates.
 *
 * EXPIRED is shown and nothing more. What happens to an expired retailer's ordering (D15) and
 * held pieces (D85) is undecided, so this screen describes the state and acts on none of it.
 *
 * Reading is `retailer.view`. Recording needs `subscription.manage`; its controls are hidden
 * without it rather than shown and refused.
 */

type Status = 'ACTIVE' | 'DUE_FOR_RENEWAL' | 'EXPIRED' | 'CANCELLED' | 'NONE';

interface Reading {
  status: Status;
  startedOn: string | null;
  renewsOn: string | null;
  daysToRenewal: number | null;
  currentTermId: string | null;
}
interface ListRow extends Reading {
  retailerId: string;
  retailerName: string;
  accountStatus: string;
  current: { fee: string; currency: string; paid: boolean } | null;
}
interface Term {
  id: string; termStart: string; termEnd: string; fee: string; currency: string;
  feePaidAt: string | null; feeReference: string | null;
  cancelledAt: string | null; cancellationReason: string | null; createdAt: string;
}
interface History extends Reading {
  retailerId: string; retailerName: string; today: string; terms: Term[];
  changes: Array<{ at: string; action: string; actorEmail: string | null; objectId: string; reason: string | null }>;
}

const TONE: Record<Status, Tone> = {
  ACTIVE: 'done', DUE_FOR_RENEWAL: 'run', EXPIRED: 'stop', CANCELLED: 'lapse', NONE: 'them',
};
const FILTERS: Array<Status | 'ALL'> = ['ALL', 'DUE_FOR_RENEWAL', 'EXPIRED', 'ACTIVE', 'CANCELLED', 'NONE'];

function StatusChip({ status }: { status: Status }) {
  const { t } = useTranslation();
  return <Chip tone={TONE[status]}>{t(`subscriptions.status.${status}`)}</Chip>;
}

export function Subscriptions() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [filter, setFilter] = useState<Status | 'ALL'>('ALL');
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get<{ today: string; timezone: string; retailers: ListRow[] }>('/subscriptions'),
  });

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  const rows = filter === 'ALL' ? data.retailers : data.retailers.filter((r) => r.status === filter);
  const count = (s: Status) => data.retailers.filter((r) => r.status === s).length;

  return (
    <>
      <PageHeader
        title={t('subscriptions.title')}
        sub={t('subscriptions.sub', { today: data.today, timezone: data.timezone })}
      />
      <div style={{ display: 'flex', gap: 'var(--mk-space-2)', flexWrap: 'wrap', marginBottom: 'var(--mk-space-4)' }}>
        {FILTERS.map((f) => (
          <Button key={f} size="sm" tone={filter === f ? 'solid' : 'ghost'} onClick={() => setFilter(f)}>
            {f === 'ALL'
              ? t('subscriptions.filterAll', { n: data.retailers.length })
              : `${t(`subscriptions.status.${f}`)} · ${count(f)}`}
          </Button>
        ))}
      </div>
      <Panel>
        <Table
          rows={rows}
          rowKey={(r) => r.retailerId}
          filterLabel={t('subscriptions.filter')}
          filter={(r) => [r.retailerName, r.status].filter(Boolean).join(' ')}
          rowClassName={(r) => (r.retailerId === open ? 'on' : undefined)}
          empty={<Empty title={t('subscriptions.emptyTitle')} body={t('subscriptions.emptyBody')} />}
          columns={[
            {
              header: t('subscriptions.colRetailer'),
              render: (r) => (
                <button type="button" className="mk-linkbtn" onClick={() => setOpen(r.retailerId === open ? null : r.retailerId)}>
                  {r.retailerName}
                </button>
              ),
            },
            { header: t('subscriptions.colStatus'), center: true, render: (r) => <StatusChip status={r.status} /> },
            { header: t('subscriptions.colStarted'), render: (r) => r.startedOn ?? '—' },
            {
              header: t('subscriptions.colRenews'),
              render: (r) => r.renewsOn
                ? <>{r.renewsOn}{r.daysToRenewal !== null && r.daysToRenewal >= 0
                  ? <span className="mk-dim"> · {t('subscriptions.inDays', { n: r.daysToRenewal })}</span> : null}</>
                : '—',
            },
            {
              header: t('subscriptions.colFee'), numeric: true,
              render: (r) => r.current ? <Money amount={r.current.fee} currency={r.current.currency} /> : '—',
            },
            {
              header: t('subscriptions.colPaid'), center: true,
              render: (r) => r.current
                ? <Chip tone={r.current.paid ? 'done' : 'stop'}>{r.current.paid ? t('subscriptions.paid') : t('subscriptions.unpaid')}</Chip>
                : null,
            },
          ]}
        />
      </Panel>
      {open ? <RetailerSubscription retailerId={open} canManage={can('subscription.manage')} /> : null}
      <Note>{t('subscriptions.sb06')}</Note>
    </>
  );
}

function RetailerSubscription({ retailerId, canManage }: { retailerId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['subscriptions', retailerId];
  const { data, isLoading, error } = useQuery({
    queryKey: key, queryFn: () => api.get<History>(`/subscriptions/${retailerId}`),
  });
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Typed from the contract, not restated. `zRecordTerm` is the same definition the route
   * validates with, so a field renamed there stops compiling here instead of failing at
   * runtime with a 400 the form cannot explain.
   */
  const [draft, setDraft] = useState<RecordTerm>({ termStart: '', termEnd: '', fee: '', paid: true, feeReference: '' });
  const [paying, setPaying] = useState<{ id: string; ref: string } | null>(null);
  const [cancelling, setCancelling] = useState<{ id: string; reason: string } | null>(null);

  async function act(run: () => Promise<unknown>) {
    setFailure(null);
    setBusy(true);
    try {
      await run();
      setPaying(null);
      setCancelling(null);
      await invalidateAll(qc, [key, ['subscriptions']]);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : t('subscriptions.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  /* A renewal starts the day the last term ends, so the form offers that by default. */
  const last = data.terms[data.terms.length - 1];
  const suggestedStart = last?.termEnd ?? data.today;

  return (
    <Panel title={t('subscriptions.historyTitle', { name: data.retailerName })} pad>
      <p><StatusChip status={data.status} /> {data.status === 'EXPIRED' ? <span className="mk-dim">{t('subscriptions.expiredNote')}</span> : null}</p>
      {failure ? <Alert tone="stop">{failure}</Alert> : null}

      <Table
        rows={[...data.terms].reverse()}
        rowKey={(r) => r.id}
        empty={<Empty title={t('subscriptions.noTermsTitle')} body={t('subscriptions.noTermsBody')} />}
        columns={[
          { header: t('subscriptions.colTerm'), render: (r) => `${r.termStart} → ${r.termEnd}` },
          { header: t('subscriptions.colFee'), numeric: true, render: (r) => <Money amount={r.fee} currency={r.currency} /> },
          {
            header: t('subscriptions.colPaid'),
            render: (r) => r.feePaidAt
              ? <>{r.feePaidAt.slice(0, 10)} <span className="mk-dim">{r.feeReference}</span></>
              : r.cancelledAt ? '—' : paying?.id === r.id ? (
                <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
                  <input
                    aria-label={t('subscriptions.reference')} value={paying.ref}
                    onChange={(e) => setPaying({ id: r.id, ref: e.target.value })}
                  />
                  <Button size="sm" disabled={busy || !paying.ref.trim()}
                    onClick={() => void act(() => api.post(`/subscriptions/terms/${r.id}/paid`, { feeReference: paying.ref }))}>
                    {t('subscriptions.markPaid')}
                  </Button>
                </span>
              ) : canManage ? (
                <Button size="sm" tone="ghost" onClick={() => setPaying({ id: r.id, ref: '' })}>{t('subscriptions.markPaid')}</Button>
              ) : <Chip tone="stop">{t('subscriptions.unpaid')}</Chip>,
          },
          {
            header: '',
            render: (r) => r.cancelledAt
              ? <span className="mk-dim">{t('subscriptions.cancelledOn', { date: r.cancelledAt.slice(0, 10), reason: r.cancellationReason })}</span>
              : !canManage ? null : cancelling?.id === r.id ? (
                <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
                  <input
                    aria-label={t('subscriptions.reason')} value={cancelling.reason}
                    onChange={(e) => setCancelling({ id: r.id, reason: e.target.value })}
                  />
                  <Button size="sm" tone="danger" disabled={busy || cancelling.reason.trim().length < 3}
                    onClick={() => void act(() => api.post(`/subscriptions/terms/${r.id}/cancel`, { reason: cancelling.reason }))}>
                    {t('subscriptions.confirmCancel')}
                  </Button>
                  <Button size="sm" tone="ghost" onClick={() => setCancelling(null)}>{t('common.cancel')}</Button>
                </span>
              ) : (
                <Button size="sm" tone="ghost" onClick={() => setCancelling({ id: r.id, reason: '' })}>{t('subscriptions.cancelTerm')}</Button>
              ),
          },
        ]}
      />

      {canManage ? (
        <form
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--mk-space-3)', marginTop: 'var(--mk-space-4)' }}
          onSubmit={(e) => {
            e.preventDefault();
            const candidate = {
              termStart: draft.termStart || suggestedStart, termEnd: draft.termEnd, fee: draft.fee, paid: draft.paid,
              ...(draft.paid ? { feeReference: draft.feeReference } : {}),
            };
            /*
             * Checked against the CONTRACT before the request, so the form says what is wrong
             * — a fee of zero, a renewal before the start — instead of relaying a 400. It is
             * the same schema the route runs, so the two cannot disagree about what is valid;
             * this only moves the message closer to the person typing.
             */
            const parsed = zRecordTerm.safeParse(candidate);
            if (!parsed.success) {
              setFailure(parsed.error.issues.map((i) => i.message).join('; '));
              return;
            }
            void act(() => api.post(`/subscriptions/${retailerId}/terms`, parsed.data));
          }}
        >
          <Field label={t('subscriptions.termStart')}>
            <input type="date" value={draft.termStart || suggestedStart} onChange={(e) => setDraft({ ...draft, termStart: e.target.value })} />
          </Field>
          <Field label={t('subscriptions.termEnd')}>
            <input type="date" value={draft.termEnd} onChange={(e) => setDraft({ ...draft, termEnd: e.target.value })} />
          </Field>
          <Field label={t('subscriptions.fee')}>
            <input inputMode="decimal" value={draft.fee} onChange={(e) => setDraft({ ...draft, fee: e.target.value.trim() })} />
          </Field>
          <Field label={t('subscriptions.reference')} hint={t('subscriptions.referenceHint')}>
            <input value={draft.feeReference} disabled={!draft.paid} onChange={(e) => setDraft({ ...draft, feeReference: e.target.value })} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-2)' }}>
            <input type="checkbox" checked={draft.paid} onChange={(e) => setDraft({ ...draft, paid: e.target.checked })} />
            {t('subscriptions.paidNow')}
          </label>
          <div style={{ alignSelf: 'end' }}>
            <Button type="submit" disabled={busy || !draft.termEnd || !draft.fee || (draft.paid && !draft.feeReference?.trim())}>
              {t('subscriptions.recordTerm')}
            </Button>
          </div>
        </form>
      ) : null}

      {data.changes.length > 0 ? (
        <>
          <h3 style={{ marginTop: 'var(--mk-space-5)' }}>{t('subscriptions.changesTitle')}</h3>
          <ul className="mk-dim">
            {data.changes.map((c) => (
              <li key={`${c.at}-${c.objectId}-${c.action}`}>
                {c.at.slice(0, 16).replace('T', ' ')} — {t(`subscriptions.action.${c.action}`, { defaultValue: c.action })}
                {c.actorEmail ? ` · ${c.actorEmail}` : ''}{c.reason ? ` · “${c.reason}”` : ''}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Panel>
  );
}
